import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, InfluencerWithStatus, CreateInfluencerData } from '../api';
import Layout from '../components/Layout';

type Filter = 'all' | 'new' | 'existing' | 'inactive';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Modal component
function Modal({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className="card p-6 w-full max-w-lg relative z-10 animate-slide-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-bold text-ink-50">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminInfluencers() {
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState<InfluencerWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<CreateInfluencerData>({
    first_name: '',
    last_name: '',
    stage_name: '',
    storefront_id: 0,
    influencer_id: '',
    slug: '',
    email: '',
    status: 'active',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Manage login modal state
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<InfluencerWithStatus | null>(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState('');
  const [manageSuccess, setManageSuccess] = useState('');
  const [inviteResult, setInviteResult] = useState<{ url?: string; expiresAt: string } | null>(null);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [emailEdited, setEmailEdited] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [influencerToDelete, setInfluencerToDelete] = useState<InfluencerWithStatus | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    loadInfluencers();
  }, []);

  const loadInfluencers = async () => {
    try {
      const { influencers } = await api.getInfluencers();
      setInfluencers(influencers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load influencers');
    } finally {
      setLoading(false);
    }
  };

  const filteredInfluencers = influencers.filter((inf) => {
    switch (filter) {
      case 'new':
        return !inf.has_user || !inf.has_password;
      case 'existing':
        return inf.has_user && inf.has_password;
      case 'inactive':
        return inf.status === 'inactive';
      default:
        return true;
    }
  });

  const handleCreateInfluencer = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      await api.createInfluencer(formData);
      setShowCreateModal(false);
      setFormData({
        first_name: '',
        last_name: '',
        stage_name: '',
        storefront_id: 0,
        influencer_id: '',
        slug: '',
        email: '',
        status: 'active',
      });
      loadInfluencers();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create influencer');
    } finally {
      setFormLoading(false);
    }
  };

  const openManageModal = (inf: InfluencerWithStatus) => {
    setSelectedInfluencer(inf);
    setLoginEmail(inf.user_email || inf.email || '');
    setManageError('');
    setManageSuccess('');
    setInviteResult(null);
    setResetResult(null);
    setEmailEdited(false);
    setShowManageModal(true);
  };

  const handleSendInvite = async () => {
    if (!selectedInfluencer) return;
    
    if (!loginEmail) {
      setManageError('Please enter an email address');
      return;
    }
    
    setManageLoading(true);
    setManageError('');
    setManageSuccess('');
    
    try {
      const result = await api.sendInfluencerInvite(selectedInfluencer.id, loginEmail);
      setInviteResult({
        url: result.inviteUrl, // Only present in dev mode
        expiresAt: result.expiresAt,
      });
      setManageSuccess(result.message);
      setEmailEdited(false);
      loadInfluencers();
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setManageLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!selectedInfluencer || !loginEmail) return;
    
    setManageLoading(true);
    setManageError('');
    setManageSuccess('');
    
    try {
      const result = await api.updateInfluencerEmail(selectedInfluencer.id, loginEmail);
      setManageSuccess(result.message);
      setEmailEdited(false);
      loadInfluencers();
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setManageLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedInfluencer) return;
    
    setManageLoading(true);
    setManageError('');
    setManageSuccess('');
    
    try {
      const result = await api.resetInfluencerPassword(selectedInfluencer.id);
      setResetResult(result.resetUrl || result.message);
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setManageLoading(false);
    }
  };

  const handleToggleStatus = async (inf: InfluencerWithStatus) => {
    try {
      const newStatus = inf.status === 'active' ? 'inactive' : 'active';
      await api.updateInfluencer(inf.id, { status: newStatus });
      loadInfluencers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const openDeleteModal = (inf: InfluencerWithStatus) => {
    setInfluencerToDelete(inf);
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const handleDeleteInfluencer = async () => {
    if (!influencerToDelete) return;

    setDeleteLoading(true);
    setDeleteError('');

    try {
      await api.deleteInfluencer(influencerToDelete.id);
      setShowDeleteModal(false);
      setInfluencerToDelete(null);
      loadInfluencers();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete influencer');
    } finally {
      setDeleteLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Stats
  const totalInfluencers = influencers.length;
  const activeInfluencers = influencers.filter((i) => i.status === 'active').length;
  const newInfluencers = influencers.filter((i) => !i.has_user || !i.has_password).length;

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-ink-50">Influencers</h1>
            <p className="text-ink-400 mt-1">Manage influencer accounts and access</p>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Influencer
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="stat-card animate-slide-up stagger-1">
            <p className="text-ink-400 text-sm mb-1">Total</p>
            <p className="text-3xl font-display font-bold text-ink-50">{totalInfluencers}</p>
          </div>
          <div className="stat-card animate-slide-up stagger-2">
            <p className="text-ink-400 text-sm mb-1">Active</p>
            <p className="text-3xl font-display font-bold text-mint-400">{activeInfluencers}</p>
          </div>
          <div className="stat-card animate-slide-up stagger-3">
            <p className="text-ink-400 text-sm mb-1">Needs Setup</p>
            <p className="text-3xl font-display font-bold text-accent-400">{newInfluencers}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {(['all', 'new', 'existing', 'inactive'] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-accent-500 text-white'
                  : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
              }`}
            >
              {f === 'all' ? 'All' : f === 'new' ? 'New' : f === 'existing' ? 'Existing' : 'Inactive'}
            </button>
          ))}
        </div>

        {/* Error display */}
        {error && (
          <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-500/20 text-accent-400">
            {error}
          </div>
        )}

        {/* Influencers Table */}
        <div className="table-container animate-slide-up">
          <div className="table-header p-4">
            <h2 className="text-lg font-semibold text-ink-50">
              {filter === 'all' ? 'All Influencers' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Influencers`}
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredInfluencers.length === 0 ? (
            <div className="p-8 text-center text-ink-400">
              {filter === 'all' ? 'No influencers yet. Add one to get started.' : 'No influencers match this filter.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-ink-400 text-sm">
                    <th className="px-4 py-3 font-medium">Stage Name</th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Storefront ID</th>
                    <th className="px-4 py-3 font-medium">Influencer ID</th>
                    <th className="px-4 py-3 font-medium">Slug</th>
                    <th className="px-4 py-3 font-medium">Login</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Created</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInfluencers.map((inf) => (
                    <tr key={inf.id} className="table-row">
                      <td className="px-4 py-4">
                        <p className="font-medium text-ink-100">{inf.stage_name}</p>
                      </td>
                      <td className="px-4 py-4 text-ink-300">
                        {inf.first_name} {inf.last_name}
                      </td>
                      <td className="px-4 py-4">
                        <code className="text-xs bg-ink-800 px-2 py-1 rounded font-mono">
                          {inf.storefront_id}
                        </code>
                      </td>
                      <td className="px-4 py-4 text-ink-400 text-sm">
                        {inf.influencer_id}
                      </td>
                      <td className="px-4 py-4 text-ink-400 text-sm">
                        {inf.slug}
                      </td>
                      <td className="px-4 py-4">
                        {inf.has_user && inf.has_password ? (
                          <span className="badge-success">Active</span>
                        ) : inf.has_user ? (
                          <span className="badge-warning">No Password</span>
                        ) : (
                          <span className="badge-default">Not Set</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={inf.status === 'active' ? 'badge-success' : 'badge-warning'}>
                          {inf.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-ink-400 text-sm">
                        {formatDate(inf.created_at)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/admin/influencers/${inf.id}/view`)}
                            className="btn-ghost text-sm text-royal-400 hover:text-royal-300"
                            title="View influencer portal"
                          >
                            <svg className="w-4 h-4 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View
                          </button>
                          <button
                            onClick={() => openManageModal(inf)}
                            className="btn-ghost text-sm"
                          >
                            Manage
                          </button>
                          <button
                            onClick={() => handleToggleStatus(inf)}
                            className="btn-ghost text-sm"
                          >
                            {inf.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => openDeleteModal(inf)}
                            className="btn-ghost text-sm text-accent-400 hover:text-accent-300"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Influencer Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Influencer"
      >
        <form onSubmit={handleCreateInfluencer} className="space-y-4">
          {formError && (
            <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">First Name *</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="input"
                placeholder="Jane"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Last Name *</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="input"
                placeholder="Doe"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Stage Name *</label>
            <input
              type="text"
              value={formData.stage_name}
              onChange={(e) => setFormData({ ...formData, stage_name: e.target.value })}
              className="input"
              placeholder="JaneDoeCreates"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Storefront ID *</label>
              <input
                type="number"
                value={formData.storefront_id || ''}
                onChange={(e) => setFormData({ ...formData, storefront_id: parseInt(e.target.value) || 0 })}
                className="input"
                placeholder="1001"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Influencer ID *</label>
              <input
                type="text"
                value={formData.influencer_id}
                onChange={(e) => setFormData({ ...formData, influencer_id: e.target.value })}
                className="input"
                placeholder="jane-doe"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Slug *</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="input"
                placeholder="jane"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Email (optional)</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              placeholder="jane@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              className="input"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={formLoading} className="btn-primary">
              {formLoading ? 'Creating...' : 'Create Influencer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Manage Login Modal */}
      <Modal
        isOpen={showManageModal}
        onClose={() => {
          setShowManageModal(false);
          setSelectedInfluencer(null);
          setInviteResult(null);
          setResetResult(null);
          setManageError('');
          setManageSuccess('');
        }}
        title={`Manage Login: ${selectedInfluencer?.stage_name || ''}`}
      >
        {selectedInfluencer && (
          <div className="space-y-6">
            {/* Error message */}
            {manageError && (
              <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm">
                {manageError}
              </div>
            )}

            {/* Success message */}
            {manageSuccess && (
              <div className="p-3 rounded-lg bg-mint-500/10 border border-mint-500/20 text-mint-400 text-sm">
                {manageSuccess}
              </div>
            )}

            {/* Invite Result */}
            {inviteResult && (
              <div className="p-4 rounded-lg bg-mint-500/10 border border-mint-500/20">
                <p className="text-mint-400 font-medium mb-2">✓ Invite sent!</p>
                {inviteResult.url && (
                  <>
                    <p className="text-sm text-ink-400 mb-2">(Dev mode: link shown below)</p>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={inviteResult.url}
                        readOnly
                        className="input flex-1 font-mono text-xs"
                      />
                      <button onClick={() => copyToClipboard(inviteResult.url!)} className="btn-secondary">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
                <p className="text-sm text-ink-400">Expires: {formatDate(inviteResult.expiresAt)}</p>
              </div>
            )}

            {/* Reset Result */}
            {resetResult && (
              <div className="p-4 rounded-lg bg-mint-500/10 border border-mint-500/20">
                <p className="text-mint-400 font-medium mb-2">✓ Password reset email sent!</p>
                <p className="text-sm text-ink-300">{resetResult}</p>
              </div>
            )}

            {/* Account Status */}
            <div className="p-4 bg-ink-800 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-ink-400">Account Status:</span>
                {selectedInfluencer.has_user && selectedInfluencer.has_password ? (
                  <span className="badge-success">Active</span>
                ) : selectedInfluencer.has_user ? (
                  <span className="badge-warning">Invited (No Password)</span>
                ) : (
                  <span className="badge-default">No Account</span>
                )}
              </div>
              {selectedInfluencer.has_user && (
                <p className="text-xs text-ink-500">
                  Current email: {selectedInfluencer.user_email}
                </p>
              )}
            </div>

            {/* Email Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-ink-300">Email Address</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => {
                  setLoginEmail(e.target.value);
                  setEmailEdited(e.target.value !== (selectedInfluencer.user_email || selectedInfluencer.email || ''));
                }}
                className="input"
                placeholder="influencer@example.com"
              />
              {!loginEmail && (
                <p className="text-xs text-accent-400">Email is required to send an invite</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Update Email Button (only if email changed for existing user) */}
              {selectedInfluencer.has_user && emailEdited && loginEmail && (
                <button
                  onClick={handleUpdateEmail}
                  disabled={manageLoading}
                  className="btn-secondary w-full"
                >
                  {manageLoading ? 'Updating...' : 'Save Email Change'}
                </button>
              )}

              {/* Send Invite Button */}
              <button
                onClick={handleSendInvite}
                disabled={manageLoading || !loginEmail}
                className="btn-primary w-full disabled:opacity-50"
              >
                {manageLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending...
                  </span>
                ) : selectedInfluencer.has_user ? (
                  'Re-send Invite Email'
                ) : (
                  'Send Invite Email'
                )}
              </button>

              {/* Reset Password Button (only for users with password) */}
              {selectedInfluencer.has_user && selectedInfluencer.has_password && !resetResult && (
                <button
                  onClick={handleResetPassword}
                  disabled={manageLoading}
                  className="btn-ghost w-full text-ink-400 hover:text-ink-200"
                >
                  Send Password Reset Email Instead
                </button>
              )}
            </div>

            <p className="text-xs text-ink-500 text-center">
              Invite links expire in 48 hours. You can re-send anytime.
            </p>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setInfluencerToDelete(null);
          setDeleteError('');
        }}
        title="Delete Influencer"
      >
        {influencerToDelete && (
          <div className="space-y-6">
            {deleteError && (
              <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm">
                {deleteError}
              </div>
            )}

            <div className="p-4 bg-accent-500/10 border border-accent-500/20 rounded-lg">
              <p className="text-accent-400 font-medium mb-2">⚠️ This action cannot be undone</p>
              <p className="text-sm text-ink-300">
                You are about to delete <strong>{influencerToDelete.stage_name}</strong> ({influencerToDelete.first_name} {influencerToDelete.last_name}).
              </p>
            </div>

            <div className="text-sm text-ink-400 space-y-2">
              <p>This will:</p>
              <ul className="list-disc list-inside space-y-1 text-ink-500">
                <li>Delete the influencer profile</li>
                {influencerToDelete.has_user && (
                  <li>Delete their user account and all login credentials</li>
                )}
                <li>Preserve historical order data (orders will show as unattributed)</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setInfluencerToDelete(null);
                }}
                className="btn-secondary flex-1"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteInfluencer}
                disabled={deleteLoading}
                className="btn-primary flex-1 !bg-accent-500 hover:!bg-accent-600"
              >
                {deleteLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Deleting...
                  </span>
                ) : (
                  'Delete Influencer'
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
}

